import { Component } from '@theme/component';

/** Time the panel stays open after the pointer leaves, so you can reach the links. */
const CLOSE_DELAY_MS = 400;

/**
 * Desktop mega menu. Opens on hover/focus and waits briefly before closing
 * so the pointer can cross the gap between the top-level item and the panel.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu]
 * @property {HTMLElement} [menuitem]
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  /** @type {HTMLElement | null} */
  #activeItem = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #closeTimer = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeydown);
    this.#clearCloseTimer();
  }

  /**
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item) return;

    this.#clearCloseTimer();

    if (item === this.#activeItem) {
      this.#measureBridge(item);
      return;
    }

    if (this.#activeItem) this.#closeItem(this.#activeItem);

    this.#activeItem = item;
    if (item.hasAttribute('aria-expanded')) {
      item.setAttribute('aria-expanded', 'true');
    }
    this.#measureBridge(item);

    const submenu = findSubmenu(item);
    if (submenu) submenu.dataset.active = '';
  };

  /**
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate = (event) => {
    if (!(event.target instanceof Element)) return;

    const listItem = event.target.closest('.menu-list__list-item');
    const item = listItem ? findMenuItem(listItem) : findMenuItem(event.target);
    if (!item || item !== this.#activeItem) return;

    const related = event.relatedTarget;
    if (related instanceof Node && listItem instanceof HTMLElement && listItem.contains(related)) {
      return;
    }

    const nextItem =
      related instanceof Element ? findMenuItem(related) : null;
    if (nextItem && nextItem !== item) {
      return;
    }

    this.#clearCloseTimer();
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = null;
      if (this.#activeItem === item) this.#closeItem(item);
    }, CLOSE_DELAY_MS);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#activeItem) return;
    this.#closeItem(this.#activeItem);
  };

  /**
   * @param {HTMLElement} item
   */
  #closeItem(item) {
    this.#clearCloseTimer();
    if (item.hasAttribute('aria-expanded')) {
      item.setAttribute('aria-expanded', 'false');
    }
    item.style.removeProperty('--box-height');
    item.style.removeProperty('--bridge-left');
    item.style.removeProperty('--bridge-width');
    item.style.removeProperty('--bridge-transform');

    const submenu = findSubmenu(item);
    if (submenu) delete submenu.dataset.active;

    if (this.#activeItem === item) this.#activeItem = null;
  }

  /**
   * Bridge height = leftover space under the link down to the header bottom,
   * plus a small overlap so the panel can be reached.
   * @param {HTMLElement} item
   */
  #measureBridge(item) {
    const header = this.closest('header-component');
    if (!header) return;

    const overlap = header.hasAttribute('data-submenu-overlap-bottom-row')
      ? header.querySelector('.header__row--top')
      : header;
    const boundary = overlap instanceof HTMLElement ? overlap : header;
    const itemRect = item.getBoundingClientRect();
    const gap = Math.max(boundary.getBoundingClientRect().bottom - itemRect.bottom, 0);

    item.style.setProperty('--box-height', `${gap + 16}px`);
    item.style.setProperty('--bridge-left', `${-Math.round(itemRect.left)}px`);
    item.style.setProperty('--bridge-width', `${document.documentElement.clientWidth}px`);
    item.style.setProperty('--bridge-transform', 'none');
  }

  #clearCloseTimer() {
    if (this.#closeTimer === null) return;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * @param {Element} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  const listItem = element.closest('.menu-list__list-item') ?? element;
  const item = listItem.querySelector('[ref="menuitem"]');
  return item instanceof HTMLElement ? item : null;
}

/**
 * @param {Element} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const listItem = element.closest('.menu-list__list-item');
  const submenu = listItem?.querySelector(':scope > [ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
